import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, UtilityService } from 'app/core/core.module';
import { Dealer } from 'app/core/model/dealer';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PaymentCreditDialogComponent } from '../payment-credit-dialog/payment-credit-dialog.component';
import { BulkEditDialogComponent } from '../bulk-edit-dialog/bulk-edit-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { Note } from 'app/core/model/note';
import { DealerNoteDialogComponent } from '../dealer-note-dialog/dealer-note-dialog.component';

@Component({
    selector: 'app-dealer-search',
    templateUrl: './dealer-search.component.html',
    styleUrls: ['./dealer-search.component.scss'],
    standalone: false,
})
export class DealerSearchComponent implements OnInit {
    dealers: any;
    showAll = false;
    currentYear: number;
    canSeeSales = false;
    canSeeAdmin = false;
    ready = false;
    uniqueYears: any[];
    filterSalesYear: any;
    salesYtd: any[];
    yearlySales: any[];
    salesCaption = '';

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;
    onOptionChangedShared = SharedModule.onOptionChanged;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        private utility: UtilityService,
    ) {}

    async ngOnInit() {
        this.setPermissions();
        this.currentYear = moment(new Date()).year().valueOf();
        const salesReps = this.uow.lookups.salesReps;
        await this.getData();
        this.ready = true;
    }

    async getData() {
        this.dealers = await this.uow.getDealers(this.showAll);

        if (!this.canSeeSales) return;
        this.salesYtd = await this.uow.getYTDSalesOrders();
        this.yearlySales = await this.uow.getYearlySalesTotals();
        const dealerSales = await this.uow.getYTDTierQualifyingSales();

        this.uniqueYears = Array.from(new Set(this.yearlySales.map((s) => s.year)));
        this.uniqueYears.unshift('All');
        this.filterSalesYear = this.uniqueYears.includes(this.currentYear) ? this.currentYear : this.uniqueYears[this.uniqueYears.length - 1];
        this.dealers.forEach((dealer) => {
            const sales = this.salesYtd.find((sales) => sales.dealerId == dealer.id);
            dealer.sales = sales?.total ?? 0;

            const tierId = dealerSales.find((ds) => ds.dealerId == dealer.id)?.calculatedPricingTierId ?? 1;
            dealer.calculatedPricingTier = this.uow.lookups.pricingTiers.find((pt) => pt.id === tierId)?.name ?? null;

            let loadedMilesHtml = '<table><tbody>';
            if (dealer.shipAddresses.length == 0 || dealer.shipSameAsBilling) {
                loadedMilesHtml += `<tr><td>${dealer.companyName} - ${dealer.billCity} - ${dealer.loadedMiles}</td></tr>`;
            } else {
                dealer.shipAddresses.forEach((address) => {
                    loadedMilesHtml += `<tr><td>${address.location ? address.location : dealer.companyName} - ${address.city} - ${address.loadedMiles}</td></tr>`;
                });
            }
            loadedMilesHtml += '</tbody></table>';
            dealer.loadedMilesHtml = loadedMilesHtml;
        });
        this.setSalesData();
        console.log(this.dealers);
    }

    setSalesData() {
        if (!this.canSeeSales) return;

        this.dealers.forEach((dealer) => {
            const yearlySales = this.yearlySales.filter((sales) => sales.dealerId == dealer.id && (this.filterSalesYear === 'All' || sales.year == this.filterSalesYear));
            let total = 0;
            if (!yearlySales) {
                dealer.yearlySales = 0;
                return;
            }
            yearlySales.forEach((sale) => {
                total += sale.total;
            });
            dealer.yearlySales = total;
        });
    }

    setPermissions() {
        this.canSeeSales = this.auth.hasRole('Dealers:CanSeeSales') || this.auth.hasRole('Dealers:Admin');
        this.canSeeAdmin = this.auth.hasRole('Dealers:Admin') || this.auth.hasRole('Administrator');
    }

    async delete(selection: any) {
        const result = await (confirm(`Are you sure you want to DELETE this dealer?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!!result) {
            selection.forEach((row) => {
                row.deleted = new Date();
            });
            await this.uow.commit();
            await this.getData();
        }
    }

    salesYearChanged(e) {
        if (!this.filterSalesYear) return;
        this.setSalesData();
        this.salesCaption = this.filterSalesYear === 'All' ? 'Total Sales' : `${this.filterSalesYear} Sales`;
    }

    selectionChange(e) {}

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }
    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.channelPartner) e.rowElement.className = e.rowElement.className + ' channel-partner';
        }
    }

    async open(dealerId: number) {
        this.router.navigate(['/dealers/overview/' + (!!dealerId ? dealerId : 'new')]);
    }

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
        this.open(e.data.id);
    }

    async toggleShowAll() {
        this.dataGrid.instance.clearSelection();
        this.showAll = !this.showAll;
        await this.getData();
    }

    async emailDealers(rows) {
        const emails = rows.flatMap((x) => (x.email !== null && x.email?.trim() !== '' ? [x.email] : []));

        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'Dealer',
                body: emails.join(';'),
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                const emailSent = await this.utility.emailReport(result.value);
                this.appInfo.pleaseWaitHide();
                await alert(emailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    async bulkEdit(rows) {
        const modalRef = this.modalService.show(BulkEditDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                rowCount: rows.length,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                for (const row of rows) {
                    if (result.value.clearSalesRep) {
                        row.salesRepId = null;
                    } else if (result.value.salesRepId) {
                        row.salesRepId = result.value.salesRepId;
                    }

                    if (result.value.clearPastDue) {
                        row.pastDue = null;
                    } else if (result.value.pastDue !== undefined) {
                        row.pastDue = result.value.pastDue;
                    }

                    if (result.value.inactiveStatus?.value === 'inactive') {
                        row.inactive = new Date();
                    } else if (result.value.inactiveStatus?.value === 'active') {
                        row.inactive = null;
                    }

                    if (result.value.clearPricingTier) {
                        row.pricingTierId = null;
                    } else if (result.value.pricingTierId) {
                        row.pricingTierId = result.value.pricingTierId;
                    }
                }
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
        });
    }

    // async toggleInactive(e) {
    //     const selectedRows = this.dataGrid.instance.getSelectedRowsData();
    //     if (!selectedRows) return;

    //     this.appInfo.pleaseWaitShow();

    //     const toggle = _.some(selectedRows, 'inactive');
    //     e.forEach((row) => {
    //         if (toggle) row.inactive = null;
    //         else row.inactive = new Date();
    //     });
    //     await this.uow.commit();
    //     await this.getData();
    //     this.appInfo.pleaseWaitHide();
    // }

    // async clearPastDue(e) {
    //     const result = await (confirm(`Are you sure you want to clear the selected dealers as past due?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
    //     if (!!result) {
    //         e.forEach((row) => {
    //             row.pastDue = null;
    //         });
    //         await this.uow.commit();
    //         await this.getData();
    //     }
    // }

    // async markPastDue(e) {
    //     const result = await (confirm(`Are you sure you want to mark the selected dealers as past due?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
    //     if (!!result) {
    //         e.forEach((row) => {
    //             row.pastDue = new Date();
    //         });
    //         await this.uow.commit();
    //         await this.getData();
    //     }
    // }

    async addDealerNote(selectedRows: Dealer) {
        const dealerId = selectedRows[0].id;
        const modalRef = this.modalService.show(DealerNoteDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                note: null,
                dealerId: dealerId,
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async createCredit(dealer: Dealer) {
        if (!dealer) return;

        const modalRef = this.modalService.show(PaymentCreditDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dealerId: dealer.id,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
        });
    }
}
